import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { formatRelativeTime } from '../lib/utils';
import {
  Settings,
  Shield,
  Sliders,
  CheckCircle,
  Eye,
  Bell,
  Upload,
  Volume2,
  Play,
  Trash2,
} from 'lucide-react';
import { useAlertsStore } from '../store/alerts';
// Same hashed-asset import as ConversionAlertProvider — keeps the preview
// and the actual notification in sync. Previously this was '/notify.mp3'
// (public/), which 404'd after the file moved to src/assets/.
import notifyMp3 from '../assets/notify.mp3';

interface SettingRow {
  key: string;
  value: string;
  description?: string;
  updatedAt?: number;
}

type SettingsMap = Record<string, SettingRow>;

// Toggle component — cyberpunk styled
function CyberToggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-sm border transition-all duration-200',
        checked
          ? 'border-neon-cyan bg-neon-cyan/10 shadow-[0_0_8px_rgba(0,255,255,0.4)]'
          : 'border-border-subtle bg-bg-secondary',
        disabled ? 'opacity-40 cursor-not-allowed' : '',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-4 w-4 transform rounded-sm transition-transform duration-200 ease-in-out',
          checked
            ? 'translate-x-6 bg-neon-cyan shadow-[0_0_6px_rgba(0,255,255,0.8)]'
            : 'translate-x-1 bg-text-tertiary',
        ].join(' ')}
      />
    </button>
  );
}

// Save indicator — auto-fades after 2 s
function SavedIndicator({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="flex items-center gap-1 text-xs font-mono text-neon-green animate-fade-in">
      <CheckCircle size={12} />
      saved
    </span>
  );
}

// Updated-at hint line
function UpdatedHint({ ts }: { ts?: number }) {
  if (!ts) return null;
  return (
    <p className="text-[10px] font-mono text-text-tertiary mt-1">
      updated {formatRelativeTime(ts)}
    </p>
  );
}

// ─── Conversion Alert Settings ──────────────────────────────────────────
/**
 * Per-user notification preferences. Stored in localStorage (not D1) — these
 * are user-device preferences, not account state.
 *
 * Custom audio uploaded as base64 data URL. Limit 1MB to keep localStorage
 * happy (5-10MB total quota).
 */
function ConversionAlertSettings() {
  const enabled = useAlertsStore((s) => s.enabled);
  const setEnabled = useAlertsStore((s) => s.setEnabled);
  const audioPref = useAlertsStore((s) => s.audioPref);
  const setAudioPref = useAlertsStore((s) => s.setAudioPref);
  const audioBlob = useAlertsStore((s) => s.audioBlob);
  const setAudioBlob = useAlertsStore((s) => s.setAudioBlob);
  const audioVolume = useAlertsStore((s) => s.audioVolume);
  const setAudioVolume = useAlertsStore((s) => s.setAudioVolume);
  const flashSubId = useAlertsStore((s) => s.flashSubId);
  // D11: surface QuotaExceededError from writeLS so user knows audio won't persist
  const audioBlobStorageFailed = useAlertsStore(
    (s) => s.audioBlobStorageFailed,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  function handleFile(file: File) {
    setUploadError(null);
    if (!file.type.startsWith('audio/')) {
      setUploadError('File harus audio (mp3/ogg/wav).');
      return;
    }
    if (file.size > 1_000_000) {
      setUploadError(
        `File terlalu besar (${(file.size / 1024).toFixed(0)}KB) — max 1MB.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setAudioBlob(dataUrl);
      setAudioPref('custom');
    };
    reader.onerror = () => setUploadError('Gagal baca file.');
    reader.readAsDataURL(file);
  }

  function preview() {
    const src = audioPref === 'custom' && audioBlob ? audioBlob : notifyMp3;
    const audio = new Audio(src);
    audio.volume = audioVolume;
    audio.play().catch((e) => setUploadError(`Play failed: ${e.message}`));
    // Also flash a fake sub_id on this page just to preview the lightning
    flashSubId('PREVIEW-SUB');
  }

  return (
    <Card>
      <h2 className="font-display text-sm uppercase tracking-wider text-neon-magenta flex items-center gap-2 mb-5">
        <Bell size={14} />
        Conversion Alerts
      </h2>

      <div className="space-y-5">
        {/* Master toggle */}
        <div className="flex items-center justify-between py-1">
          <div>
            <p className="text-sm font-display uppercase tracking-wide text-text-primary">
              Enable Notifications
            </p>
            <p className="text-xs font-mono text-text-tertiary">
              Polling every 5s · plays sound + flashes sub_id when new
              conversion arrives
            </p>
          </div>
          <CyberToggle checked={enabled} onChange={setEnabled} />
        </div>

        {/* Audio source */}
        <div className="pt-4 border-t border-border-subtle">
          <p className="text-[10px] font-display uppercase tracking-wider text-text-secondary mb-2">
            Notification sound
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={() => setAudioPref('default')}
              className={`text-[11px] font-mono px-3 py-1.5 rounded border ${
                audioPref === 'default'
                  ? 'border-neon-cyan text-neon-cyan bg-neon-cyan/10'
                  : 'border-border-subtle text-text-secondary hover:border-border-strong'
              }`}
            >
              Default (syududu)
            </button>
            <button
              type="button"
              onClick={() => setAudioPref('custom')}
              disabled={!audioBlob}
              className={`text-[11px] font-mono px-3 py-1.5 rounded border disabled:opacity-40 ${
                audioPref === 'custom'
                  ? 'border-neon-magenta text-neon-magenta bg-neon-magenta/10'
                  : 'border-border-subtle text-text-secondary hover:border-border-strong'
              }`}
            >
              Custom upload
            </button>
          </div>

          {/* Upload */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-neon-cyan border border-neon-cyan/40 px-3 py-1.5 rounded hover:bg-neon-cyan/10">
                <Upload size={12} />
                {audioBlob
                  ? 'Replace audio file'
                  : 'Upload audio file (mp3/ogg/wav, max 1MB)'}
              </span>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
                className="hidden"
              />
            </label>
            {audioBlob && (
              <div className="flex items-center gap-2 text-[10px] font-mono text-text-tertiary">
                <CheckCircle size={10} className="text-neon-green" />
                Custom audio loaded ({(audioBlob.length / 1024).toFixed(0)}KB)
                <button
                  type="button"
                  onClick={() => {
                    setAudioBlob(null);
                    setAudioPref('default');
                  }}
                  className="text-neon-red hover:opacity-70"
                  title="Remove custom audio"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            )}
            {uploadError && (
              <p className="text-[10px] font-mono text-neon-red">
                {uploadError}
              </p>
            )}
            {/* D11: QuotaExceededError — audio loaded in memory but not persisted */}
            {audioBlobStorageFailed && !uploadError && (
              <p className="text-[10px] font-mono text-neon-yellow">
                Audio file too large for browser storage — will work this
                session but won't survive a reload. Try a smaller file
                (&lt;500KB).
              </p>
            )}
          </div>
        </div>

        {/* Volume */}
        <div className="pt-4 border-t border-border-subtle">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-display uppercase tracking-wider text-text-secondary">
              <Volume2 size={10} className="inline mr-1" />
              Volume
            </p>
            <span className="text-[10px] font-mono text-neon-cyan">
              {Math.round(audioVolume * 100)}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={audioVolume}
            onChange={(e) => setAudioVolume(Number(e.target.value))}
            className="w-full accent-neon-cyan"
          />
        </div>

        {/* Test button */}
        <div className="pt-4 border-t border-border-subtle flex items-center gap-2">
          <Button
            onClick={preview}
            size="sm"
            icon={<Play size={12} />}
            variant="magenta"
          >
            Test alert
          </Button>
          <span className="text-[10px] font-mono text-text-tertiary">
            Plays sound + flashes a "PREVIEW-SUB" lightning effect
          </span>
        </div>
      </div>
    </Card>
  );
}

export function SettingsPage() {
  const qc = useQueryClient();

  const { data: rawSettings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiClient.get<SettingRow[]>('/api/v1/settings'),
  });

  // Index by key for easy lookup
  const settings: SettingsMap = {};
  for (const s of rawSettings ?? []) {
    settings[s.key] = s;
  }

  // ─── Self-Signup card state ───────────────────────────────────────────────
  const [allowSelfSignup, setAllowSelfSignup] = useState(false);
  const [savedSignup, setSavedSignup] = useState(false);

  // ─── Defaults card state ─────────────────────────────────────────────────
  const [attrWindow, setAttrWindow] = useState('30');
  const [holdPeriod, setHoldPeriod] = useState('24');
  const [botThreshold, setBotThreshold] = useState('30');
  const [currency, setCurrency] = useState('USD');
  const [timezone, setTimezone] = useState('UTC');
  const [savedDefaults, setSavedDefaults] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sync local state once data loads
  useEffect(() => {
    if (!rawSettings) return;
    setAllowSelfSignup(settings['allow_self_signup']?.value === 'true');
    setAttrWindow(settings['default_attribution_window_days']?.value ?? '30');
    setHoldPeriod(settings['default_hold_period_hours']?.value ?? '24');
    setBotThreshold(settings['default_bot_threshold']?.value ?? '30');
    setCurrency(settings['default_currency']?.value ?? 'USD');
    setTimezone(settings['default_timezone']?.value ?? 'UTC');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSettings]);

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, string>) =>
      apiClient.patch('/api/v1/settings', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  });

  async function saveSignup() {
    await saveMutation.mutateAsync({
      allow_self_signup: String(allowSelfSignup),
    });
    setSavedSignup(true);
    setTimeout(() => setSavedSignup(false), 2000);
  }

  function validateDefaults(): boolean {
    const errs: Record<string, string> = {};
    const numCheck = (v: string, key: string, label: string) => {
      const n = Number(v);
      if (v === '' || isNaN(n) || n < 0) errs[key] = `${label} must be ≥ 0`;
    };
    numCheck(
      attrWindow,
      'default_attribution_window_days',
      'Attribution window',
    );
    numCheck(holdPeriod, 'default_hold_period_hours', 'Hold period');
    numCheck(botThreshold, 'default_bot_threshold', 'Bot threshold');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function saveDefaults() {
    if (!validateDefaults()) return;
    await saveMutation.mutateAsync({
      default_attribution_window_days: attrWindow,
      default_hold_period_hours: holdPeriod,
      default_bot_threshold: botThreshold,
      default_currency: currency,
      default_timezone: timezone,
    });
    setSavedDefaults(true);
    setTimeout(() => setSavedDefaults(false), 2000);
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest">
            SETTINGS
          </h1>
          <p className="text-sm text-text-secondary font-mono">
            // global system configuration
          </p>
        </div>
        <p className="text-text-tertiary font-mono text-sm">loading...</p>
      </div>
    );
  }

  return (
    // Layout strategy:
    //   - max-w-7xl so wide monitors don't stretch forms beyond readable width,
    //     but mid-size laptops (~1280px) get the full content area.
    //   - lg:grid-cols-2 for the top row: Self-Signup + System are small cards
    //     that fit comfortably side-by-side. Defaults and Conversion Alerts
    //     keep full width because they have inner 2-col grids that need room.
    <div className="space-y-6 animate-fade-in max-w-7xl">
      <div>
        <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
          <Settings size={20} className="text-neon-cyan" />
          SETTINGS
        </h1>
        <p className="text-sm text-text-secondary font-mono">
          // global system configuration
        </p>
      </div>

      {/* Top row: small cards side-by-side on lg+ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* ── Self-Signup ─────────────────────────────────────────────────── */}
        <Card>
          <h2 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2 mb-5">
            <Shield size={14} />
            Self-Signup
          </h2>

          <div className="flex items-center justify-between py-2">
            <div className="space-y-0.5">
              <p className="text-sm text-text-primary font-display uppercase tracking-wide">
                Allow Public Registration
              </p>
              <p className="text-xs text-text-tertiary font-mono">
                {settings['allow_self_signup']?.description ??
                  'Allow users to create their own accounts'}
              </p>
              <UpdatedHint ts={settings['allow_self_signup']?.updatedAt} />
            </div>
            <CyberToggle
              checked={allowSelfSignup}
              onChange={setAllowSelfSignup}
            />
          </div>

          <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border-subtle">
            <Button onClick={saveSignup} loading={saveMutation.isPending}>
              Save
            </Button>
            <SavedIndicator show={savedSignup} />
          </div>
        </Card>

        {/* ── System (read-only) — moved up to pair with Self-Signup ────────── */}
        <Card>
          <h2 className="font-display text-sm uppercase tracking-wider text-neon-yellow flex items-center gap-2 mb-5">
            <Eye size={14} />
            System
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
                JWT Issuer
              </label>
              <div className="input-cyber bg-bg-tertiary text-text-secondary font-mono text-sm cursor-not-allowed select-all">
                {settings['jwt_issuer']?.value ?? 'rj-tracking'}
              </div>
              <p className="text-xs text-text-tertiary mt-1 font-mono">
                {settings['jwt_issuer']?.description ??
                  'Issuer claim used in signed tokens — read only'}
              </p>
              <UpdatedHint ts={settings['jwt_issuer']?.updatedAt} />
            </div>
          </div>
        </Card>
      </div>

      {/* ── Defaults ────────────────────────────────────────────────────────── */}
      <Card>
        <h2 className="font-display text-sm uppercase tracking-wider text-neon-magenta flex items-center gap-2 mb-5">
          <Sliders size={14} />
          Defaults
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Input
              label="Attribution Window (days)"
              type="number"
              min={0}
              value={attrWindow}
              onChange={(e) => setAttrWindow(e.target.value)}
              hint="Klik → konversi match window. Network biasanya pakai 30 hari."
              error={errors['default_attribution_window_days']}
            />
            <UpdatedHint
              ts={settings['default_attribution_window_days']?.updatedAt}
            />
          </div>

          <div>
            <Input
              label="Bot Score Threshold (0–100)"
              type="number"
              min={0}
              max={100}
              value={botThreshold}
              onChange={(e) => setBotThreshold(e.target.value)}
              hint="Cloudflare bot score di bawah ini dianggap bot. 30 = strict, 50 = moderate."
              error={errors['default_bot_threshold']}
            />
            <UpdatedHint ts={settings['default_bot_threshold']?.updatedAt} />
          </div>

          <div>
            <Input
              label="Default Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              hint="ISO-4217 e.g. USD, EUR, IDR"
            />
            <UpdatedHint ts={settings['default_currency']?.updatedAt} />
          </div>

          <div>
            <Input
              label="Default Timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              hint="Reporting default is UTC. Keep this UTC unless you intentionally change the system default for new users."
            />
            <UpdatedHint ts={settings['default_timezone']?.updatedAt} />
          </div>

          <div className="sm:col-span-2 px-4 py-3 border border-border-subtle rounded-sm bg-bg-tertiary">
            <div className="flex items-start gap-2">
              <span className="text-text-tertiary text-xs">ℹ️</span>
              <div className="text-[11px] font-mono text-text-secondary leading-relaxed">
                <strong className="text-text-primary">Hold period</strong>{' '}
                di-skip — sistem auto-approve postback dari network. Jika
                network kirim explicit{' '}
                <code className="text-neon-yellow">status=pending</code>, hold
                period default {holdPeriod}h diterapkan untuk record itu saja.
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border-subtle">
          <Button
            variant="magenta"
            onClick={saveDefaults}
            loading={saveMutation.isPending}
          >
            Save
          </Button>
          <SavedIndicator show={savedDefaults} />
        </div>
      </Card>

      {/* ── Conversion Alerts ─────────────────────────────────────────────── */}
      <ConversionAlertSettings />
    </div>
  );
}
